import {
  ComponentClass,
  StatelessComponent,
  ReactElement,
  cloneElement,
  CSSProperties,
  Component,
  createElement,
  PropTypes,
} from "react";
import Prefixer from "inline-style-prefixer";
import {mapValues} from "lodash";
import {Dispatch} from "redux";


const prefixer = new Prefixer({
  userAgent: navigator.userAgent,
});


export function prepareStyles<T extends {
  [key: string]: CSSProperties;
}>(styles: T): T {
  return mapValues(styles, style => prefixer.prefix(style)) as T;
}


export type WrappedComponent<P> = StatelessComponent<P> | ComponentClass<P>;
export type HOC<P, R> = (component: WrappedComponent<P>) => ComponentClass<R>;
export type Enhancer<R> =
  <P>(component: WrappedComponent<P & R>) => ComponentClass<P>;


const isSpecified = (obj: any) => obj != null;

export function mergeStyles(
  defaultStyle: CSSProperties,
  ...styles: (CSSProperties | undefined)[],
): CSSProperties {
  return styles.some(isSpecified)
    ? Object.assign({}, defaultStyle, ...styles)
    : defaultStyle;
}

/**
 * Return new element with added style.
 *
 * Useful with `react-router` to style child components.
 */
export function cloneElementWithStyle(
  element: ReactElement<{style: CSSProperties}>, style?: CSSProperties,
): ReactElement<{style: CSSProperties}> {
  if (style == null)
    return element;

  return cloneElement(element, {
    style: mergeStyles(style, element.props.style),
  });
}

export function getDisplayName(component: WrappedComponent<any>) {
  return component.displayName || component['name'] || 'Component';
}


export function withLifecycleActions<P>(actionCreators: {
  didMount?(dispatch: Dispatch<any>, props: P): void;
  didUpdate?(dispatch: Dispatch<any>,
                    nextProps: P, prevProps: P): void;
  willUnmount?(dispatch: Dispatch<any>, props: P): void;
}): HOC<P, P> {
  return component => {
    const name = `WithLifecycleActions(${getDisplayName(component)})`;

    class ComponentWithLifecycleActions extends Component<P, void> {
      static displayName = name;

      static contextTypes = {
        store: PropTypes.object,
      };

      private getDispatch() {
        return this.context.store.dispatch as Dispatch<any>;
      }

      componentDidMount() {
        if (actionCreators.didMount) {
          const dispatch = this.getDispatch();

          actionCreators.didMount(dispatch, this.props);
        }
      }

      componentDidUpdate(prevProps: P) {
        if (actionCreators.didUpdate) {
          const dispatch = this.getDispatch();

          actionCreators.didUpdate(dispatch, this.props, prevProps);
        }
      }

      componentWillUnmount() {
        if (actionCreators.willUnmount) {
          const dispatch = this.getDispatch();

          actionCreators.willUnmount(dispatch, this.props);
        }
      }

      render() {
        return createElement(component as any, this.props);
      }
    }

    return ComponentWithLifecycleActions;
  };
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/utils/components.ts